import { Eye, FileText, Info } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import Textarea from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import type { TopUpRequest } from "@/types/wallet";
import { formatDate, formatINR, formatTime } from "@/pages/retailer/wallet/data";
import ReceiptPreviewModal from "./ReceiptPreviewModal";

interface ReviewTopupModalProps {
  open: boolean;
  onClose: () => void;
  request: TopUpRequest | null;
  onApprove: (request: TopUpRequest, amountToCredit: number, remark: string) => Promise<void>;
  onReject: (request: TopUpRequest, remark: string) => Promise<void>;
}

type Decision = "approve" | "reject";

export default function ReviewTopupModal({ open, onClose, request, onApprove, onReject }: ReviewTopupModalProps) {
  const toast = useToast();
  const [decision, setDecision] = useState<Decision>("approve");
  const [amountToCredit, setAmountToCredit] = useState("");
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [receiptOpen, setReceiptOpen] = useState(false);

  useEffect(() => {
    if (open && request) {
      setDecision("approve");
      setAmountToCredit(String(request.amount));
      setRemark("");
      setError("");
      setReceiptOpen(false);
    }
  }, [open, request]);

  if (!request) return null;


  function handleClose() {
    setError("");
    onClose();
  }

  async function handleConfirm() {
    if (decision === "reject" && !remark.trim()) {
      setError("A remark is required when rejecting");
      return;
    }
    if (decision === "approve" && (!amountToCredit || Number(amountToCredit) <= 0)) {
      setError("Please enter a valid amount to credit");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      if (decision === "approve") {
        await onApprove(request, Number(amountToCredit), remark.trim());
      } else {
        await onReject(request, remark.trim());
      }
      handleClose();
    } catch (err: any) {
      setError(err?.message || "Could not complete this review");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Review top-up request"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={submitting}>
            Confirm
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] text-ink-secondary">{request.requestId}</p>

        <div className="flex flex-col divide-y divide-line rounded-[10px] border border-line">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Retailer</span>
            <span className="text-right text-sm font-medium text-ink">
              {request.shopName || request.retailerName || "Retailer"}
              {(request.userCode || request.mobile) && (
                <span className="block text-xs text-ink-secondary">{[request.userCode, request.mobile].filter(Boolean).join(" · ")}</span>
              )}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Amount</span>
            <span className="text-lg font-semibold text-ink">{formatINR(request.amount)}</span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Payment Mode</span>
            <span className="text-right text-sm font-medium text-ink">{request.paymentMode}</span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Reference Number</span>
            <span className="font-mono text-sm text-ink">{request.referenceNumber}</span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Payment Date</span>
            <span className="text-sm font-medium text-ink">{formatDate(request.requestedAt)}</span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Requested On</span>
            <span className="text-sm font-medium text-ink">
              {formatDate(request.requestedAt)} · {formatTime(request.requestedAt)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="text-[13px] text-ink-secondary">Retailer Remark</span>
            <span className="text-sm text-ink">—</span>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-[10px] border border-line bg-slate-50/60 p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-primary-500">
            <FileText className="h-4 w-4" />
          </span>
          <span className="flex-1 truncate text-[13px] text-ink">{request.receiptFileName}</span>
          <Button variant="ghost" size="sm" leftIcon={<Eye className="h-4 w-4" />} onClick={() => setReceiptOpen(true)}>
            View Receipt
          </Button>
        </div>

        <div className="flex items-start gap-3 rounded-[10px] border border-info-bg bg-info-bg p-4">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-info">
            <Info className="h-5 w-5" />
          </span>
          <p className="text-[13px] text-ink-secondary">
            Verify the payment in your bank account before approving. Approving credits the retailer's wallet
            immediately.
          </p>
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-ink">
            Decision <span className="text-danger">*</span>
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setDecision("approve")}
              className={`flex flex-col items-start rounded-lg border p-4 text-left transition-colors cursor-pointer ${
                decision === "approve"
                  ? "border-primary-500 bg-primary-50"
                  : "border-slate-300 bg-white hover:border-primary-300"
              }`}
            >
              <span className={`text-sm font-semibold ${decision === "approve" ? "text-primary-600" : "text-ink"}`}>Approve</span>
              <span className="mt-0.5 text-xs text-ink-secondary">Credit the wallet</span>
            </button>
            <button
              type="button"
              onClick={() => setDecision("reject")}
              className={`flex flex-col items-start rounded-lg border p-4 text-left transition-colors cursor-pointer ${
                decision === "reject"
                  ? "border-primary-500 bg-primary-50"
                  : "border-slate-300 bg-white hover:border-primary-300"
              }`}
            >
              <span className={`text-sm font-semibold ${decision === "reject" ? "text-primary-600" : "text-ink"}`}>Reject</span>
              <span className="mt-0.5 text-xs text-ink-secondary">Decline the request</span>
            </button>
          </div>
        </div>

        {decision === "approve" && (
          <Input
            label="Amount to Credit"
            value={amountToCredit}
            onChange={(e) => setAmountToCredit(e.target.value)}
            helper="Adjust only if the received amount differs from the requested amount"
          />
        )}

        <Textarea
          label="Admin Remark"
          required={decision === "reject"}
          rows={3}
          maxLength={200}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder={decision === "reject" ? "Explain why the request is being rejected" : "Add an internal note (optional)"}
        />
        <p className="-mt-3 text-right text-xs text-ink-muted">{remark.length}/200</p>

        {error && <p className="text-[13px] text-danger">{error}</p>}
      </div>

      <ReceiptPreviewModal
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        request={request}
      />
    </Modal>
  );
}